import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import { getOperators } from "services/operatorService";

interface OperatorSingleSelectProps {
  label?: string;
  value?: string | null;
  onChange: (operatorId: string | null) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  populatedOperator?: { _id: string; operatorName: string; externalOpId?: string } | null;
}

const OperatorSingleSelect = ({
  label = "Select Operator",
  value = null,
  onChange,
  error,
  helperText,
  disabled = false,
  fullWidth = true,
  populatedOperator = null,
}: OperatorSingleSelectProps) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 20;

  // Resolve the selected option from value
  const selectedOperator = (() => {
    if (!value) return null;

    const foundInOptions = options.find((op) => op.id === value);
    if (foundInOptions) return foundInOptions;

    if (populatedOperator && populatedOperator._id === value) {
      return {
        id: populatedOperator._id,
        label: populatedOperator.externalOpId
          ? `${populatedOperator.operatorName} (${populatedOperator.externalOpId})`
          : populatedOperator.operatorName,
        ...populatedOperator,
      };
    }

    return { id: value, label: value };
  })();

  // Initialize options from populatedOperator on mount
  useEffect(() => {
    if (populatedOperator && populatedOperator._id) {
      const initialOperator = {
        id: populatedOperator._id,
        label: populatedOperator.externalOpId
          ? `${populatedOperator.operatorName} (${populatedOperator.externalOpId})`
          : populatedOperator.operatorName,
        ...populatedOperator,
      };
      setOptions([initialOperator]);
    }
  }, [populatedOperator]);

  // Fetch operators with pagination and search
  useEffect(() => {
    let active = true;

    if (!open) {
      return undefined;
    }

    (async () => {
      setLoading(true);
      try {
        const response = await getOperators({
          pageNo: 1,
          pageSize,
          search: inputValue || "",
          sortBy: "name",
          sortDirection: 1,
        });

        if (active && response?.data?.operators) {
          const newOperators = response.data.operators.map((op: any) => ({
            id: op._id,
            label: op.externalOpId
              ? `${op.operatorName} (${op.externalOpId})`
              : op.operatorName,
            ...op,
          }));

          // Ensure the currently selected operator is always in the options
          if (selectedOperator && selectedOperator.label !== selectedOperator.id) {
            const fetchedIds = new Set(newOperators.map((op: any) => op.id));
            if (!fetchedIds.has(selectedOperator.id)) {
              setOptions([selectedOperator, ...newOperators]);
            } else {
              setOptions(newOperators);
            }
          } else {
            setOptions(newOperators);
          }

          setHasMore(newOperators.length === pageSize);
          setPageNo(1);
        }
      } catch (err) {
        console.error("Failed to fetch operators:", err);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [open, inputValue]);

  // Load more operators on scroll
  const loadMoreOperators = async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const nextPage = pageNo + 1;
      const response = await getOperators({
        pageNo: nextPage,
        pageSize,
        search: inputValue || "",
        sortBy: "name",
        sortDirection: 1,
      });

      if (response?.data?.operators) {
        const newOperators = response.data.operators.map((op: any) => ({
          id: op._id,
          label: op.externalOpId
            ? `${op.operatorName} (${op.externalOpId})`
            : op.operatorName,
          ...op,
        }));

        setOptions((prev) => {
          const uniqueIds = new Set(prev.map((op) => op.id));
          const filtered = newOperators.filter((op: any) => !uniqueIds.has(op.id));
          return [...prev, ...filtered];
        });

        setHasMore(newOperators.length === pageSize);
        setPageNo(nextPage);
      }
    } catch (err) {
      console.error("Failed to fetch more operators:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle scroll for infinite loading
  const handleListboxScroll = (event: any) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;
    const bottom = listboxNode.scrollHeight - 10; // 10px threshold

    if (position >= bottom && hasMore && !loading) {
      loadMoreOperators();
    }
  };

  return (
    <Autocomplete
      fullWidth={fullWidth}
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={selectedOperator}
      onChange={(_event, newValue) => {
        if (onChange) {
          onChange(newValue?.id ?? null);
        }
      }}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue, reason) => {
        if (reason === "input") {
          setInputValue(newInputValue);
          setPageNo(1);
          setHasMore(true);
        } else if (reason === "reset") {
          // Fires when an option is selected - update to show selected label
          setInputValue(newInputValue);
        }
      }}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      getOptionLabel={(option) => option.label || option.id}
      options={options}
      loading={loading}
      loadingText="Loading operators..."
      noOptionsText="No operators found"
      ListboxProps={{
        onScroll: handleListboxScroll,
        style: { maxHeight: "400px" },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          error={Boolean(error)}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

OperatorSingleSelect.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  populatedOperator: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    operatorName: PropTypes.string.isRequired,
    externalOpId: PropTypes.string,
  }),
};

export default OperatorSingleSelect;
